import { Int32MaxValue, MaximumQuantityForThirdPartyBrands, QuantityOverflow } from './constants';

import { formatQuantity } from './formatter';

export const emptyPriceTableCheck = (priceTable) => {
  return !priceTable || priceTable.length === 0
    ? { isValid: false, message: 'No pricing found for this item.' }
    : { isValid: true, message: '' };
};

const quantityOverflowCheck = (quantity) => {
  return quantity === QuantityOverflow
    ? {
        isValid: false,
        message: `The total quantity you entered exceeds the max value(${formatQuantity(
          Int32MaxValue
        )}) of Int32. Please enter a valid quantity.`,
      }
    : { isValid: true, message: '' };
};

const maximumQuantityCheckForThirdPartyBrands = (internalBrand, quantity) => {
  return !internalBrand && quantity > MaximumQuantityForThirdPartyBrands
    ? {
        isValid: false,
        message: `The total quantity you entered exceeds the maximum allowed quantity of ${formatQuantity(
          MaximumQuantityForThirdPartyBrands
        )}. Please modify the quantity you have entered.`,
      }
    : {
        isValid: true,
        message: '',
      };
};

const commonQuantityCheck = (quantity, miniQuantity) => {
  let result;
  if (quantity === 0) {
    result = {
      isValid: false,
      message: `Please select a quantity of at least ${formatQuantity(miniQuantity)} to see your price.`,
    };
  } else if (quantity > 0 && quantity < miniQuantity) {
    result = {
      isValid: false,
      message: `Please select a quantity of at least ${formatQuantity(
        miniQuantity - quantity
      )} more to see your price. The minimum quantity is ${formatQuantity(miniQuantity)}.`,
    };
  } else {
    result = { isValid: true, message: '' };
  }
  return result;
};

const divisibleQuantityCheck = (quantity, divisorQuantity, miniQuantity) => {
  let result;
  if (quantity === 0) {
    result = {
      isValid: false,
      message: `This product can only be ordered in groups of ${formatQuantity(
        divisorQuantity
      )}. The minimum quantity is ${formatQuantity(miniQuantity)}.`,
    };
  } else if (quantity > 0 && quantity < miniQuantity) {
    result = {
      isValid: false,
      message: `This product can only be ordered in groups of ${formatQuantity(
        divisorQuantity
      )}. Please select a quantity of at least ${formatQuantity(
        miniQuantity - quantity
      )} more to see your price. The minimum quantity is ${formatQuantity(miniQuantity)}.`,
    };
  } else {
    const isValid = quantity % divisorQuantity === 0;
    const message = isValid
      ? ''
      : `This product can only be ordered in groups of ${formatQuantity(
          divisorQuantity
        )}. Please update the quantity that you entered.`;
    result = { isValid, message };
  }
  return result;
};

const exactQuantityCheck = (quantity, miniQuantity, priceTable) => {
  let result;
  if (quantity === 0) {
    result = {
      isValid: false,
      message: `This product can only be ordered in the exact quantities shown in the price block. The minimum quantity is ${formatQuantity(
        miniQuantity
      )}.`,
    };
  } else if (quantity > 0 && quantity < miniQuantity) {
    result = {
      isValid: false,
      message: `This product can only be ordered in the exact quantities shown in the price block. Please select a quantity of at least ${formatQuantity(
        miniQuantity - quantity
      )} more to see your price. The minimum quantity is ${formatQuantity(miniQuantity)}.`,
    };
  } else {
    const isValid = priceTable.findIndex((row) => row.quantity === quantity) >= 0;
    const message = isValid
      ? ''
      : `This product can only be ordered in the exact quantities shown in the price block. Please update the quantity that you entered.`;
    result = { isValid, message };
  }
  return result;
};

export const isValidQuantity = (quantity, fullPackageQuantity, miniQuantity, priceTable, internalBrand) => {
  let result = emptyPriceTableCheck(priceTable);
  if (!result.isValid) return result;

  result = quantityOverflowCheck(quantity);
  if (!result.isValid) return result;

  result = maximumQuantityCheckForThirdPartyBrands(internalBrand, quantity);
  if (!result.isValid) return result;

  const lockQtyFlag = priceTable[0].lockQtyFlag.trim().toUpperCase();
  const priceCode = priceTable[0].priceCode.trim().toUpperCase();
  switch (lockQtyFlag) {
    case 'Y':
      result = divisibleQuantityCheck(quantity, miniQuantity, miniQuantity);
      break;
    case 'E':
      result = exactQuantityCheck(quantity, miniQuantity, priceTable);
      break;
    case 'C':
      result =
        fullPackageQuantity > 0
          ? divisibleQuantityCheck(quantity, fullPackageQuantity, miniQuantity)
          : commonQuantityCheck(quantity, miniQuantity);
      break;
    default:
      result =
        priceCode === 'M' || priceCode === 'C'
          ? divisibleQuantityCheck(quantity, miniQuantity, miniQuantity)
          : commonQuantityCheck(quantity, miniQuantity);
      break;
  }
  return result;
};

export const validateLockedQuantity = (quantity, fullPackageQty, miniQty, priceTable, internalBrand) => {
  if (quantity === 0) {
    return {
      isValid: true,
      message: '',
    };
  } else if (quantity > 0 && quantity < miniQty) {
    return {
      isValid: false,
      message: 'Your quantity does not meet the minimum requirement',
    };
  } else if (quantity > Int32MaxValue) {
    return quantityOverflowCheck(QuantityOverflow);
  }

  let result = maximumQuantityCheckForThirdPartyBrands(internalBrand, quantity);
  if (!result.isValid) {
    return result;
  }

  result = emptyPriceTableCheck(priceTable);
  if (!result.isValid) {
    return result;
  }

  let divisor;
  let validationRequired = false;
  const lockQtyFlag = priceTable[0].lockQtyFlag.trim().toUpperCase();
  const priceCode = priceTable[0].priceCode.trim().toUpperCase();

  switch (lockQtyFlag) {
    case 'Y':
      divisor = miniQty;
      validationRequired = true;
      break;
    case 'C':
      if (fullPackageQty > 0) {
        divisor = fullPackageQty;
        validationRequired = true;
      }
      break;
    default:
      if (priceCode === 'M' || priceCode === 'C') {
        divisor = miniQty;
        validationRequired = true;
      }
      break;
  }

  if (!validationRequired) {
    return result;
  }

  const isValid = quantity % divisor === 0;
  const message = isValid ? '' : `This product can only be ordered in groups of ${formatQuantity(divisor)}`;
  return { isValid, message };
};
